import React, { useState, useEffect } from 'react';
import { Heart } from 'lucide-react';
import { updateLike, getLikesCount, hasLikedToday } from '../utils/likes';
import { trackLike } from '../utils/analytics';

interface LikeButtonProps {
  type: 'fact' | 'animal' | 'science' | 'excuse' | 'record' | 'invention' | 'number';
  content: string;
  onLike?: () => void;
}

const LikeButton: React.FC<LikeButtonProps> = ({ type, content, onLike }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);

  useEffect(() => {
    const initializeLikeStatus = async () => {
      try {
        const [count, liked] = await Promise.all([
          getLikesCount(type, content),
          hasLikedToday(type, content)
        ]);
        setLikesCount(count);
        setHasLiked(liked);
      } catch (error) {
        console.error('Error initializing like status:', error);
      }
    };

    initializeLikeStatus();
  }, [type, content]);

  const handleLike = async () => {
    if (isLoading || hasLiked) return;

    try {
      setIsLoading(true);
      const result = await updateLike(type, content);
      setLikesCount(result.likes_count);
      setHasLiked(true);
      trackLike(type, content);
      if (onLike) onLike();
    } catch (error) {
      console.error('Error updating like:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      onClick={handleLike}
      disabled={isLoading || hasLiked}
      className={`transition-all duration-300 flex items-center gap-1 px-3 py-1.5 rounded-full ${
        isLoading ? 'opacity-50 cursor-not-allowed' : ''
      } ${
        hasLiked 
          ? 'bg-red-500/20 text-red-400' 
          : 'text-gray-400 hover:bg-red-500/10 hover:text-red-400'
      }`}
    >
      <Heart className={`h-4 w-4 ${hasLiked ? 'fill-current' : ''}`} />
      <span className="text-sm">
        {isLoading ? 'Loading...' : likesCount > 0 ? `${likesCount} likes` : 'Like'}
      </span>
    </button>
  );
};

export default LikeButton;